import { useState, useEffect, useCallback } from "react";
import { api } from "../hooks/useAuth";

// ── Icons ────────────────────────────────────────────────────────────────────
const Icon = ({ d, size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const icons = {
  dashboard: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z M9 22V12h6v10",
  users: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75",
  media: "M15 10l4.553-2.069A1 1 0 0 1 21 8.87v6.26a1 1 0 0 1-1.447.894L15 14M3 8a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
  access: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  settings: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06-.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z",
  logout: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4 M16 17l5-5-5-5 M21 12H9",
  search: "M21 21l-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0",
  trash: "M3 6h18 M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2",
  eye: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z",
  x: "M18 6L6 18M6 6l12 12",
  refresh: "M23 4v6h-6 M1 20v-6h6 M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15",
};

// ── Toggle Switch ─────────────────────────────────────────────────────────────
function Toggle({ value, onChange, accent = "#ff4d6d", disabled = false }) {
  return (
    <div
      onClick={() => !disabled && onChange(!value)}
      style={{
        width: 44, height: 24, borderRadius: 12,
        background: value ? accent : "rgba(255,255,255,0.1)",
        cursor: disabled ? "not-allowed" : "pointer",
        position: "relative", transition: "background 0.25s",
        flexShrink: 0, opacity: disabled ? 0.5 : 1,
      }}
    >
      <div style={{
        position: "absolute", top: 3,
        left: value ? 23 : 3,
        width: 18, height: 18, borderRadius: "50%",
        background: "white",
        transition: "left 0.25s cubic-bezier(0.4,0,0.2,1)",
        boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
      }} />
    </div>
  );
}

// ── Status Badge ──────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    active: { bg: "rgba(6,214,160,0.15)", color: "#06d6a0", label: "Active" },
    inactive: { bg: "rgba(255,255,255,0.07)", color: "rgba(255,255,255,0.4)", label: "Inactive" },
    suspended: { bg: "rgba(255,68,68,0.15)", color: "#ff4444", label: "Suspended" },
    published: { bg: "rgba(6,214,160,0.15)", color: "#06d6a0", label: "Published" },
    draft: { bg: "rgba(255,214,10,0.15)", color: "#ffd60a", label: "Draft" },
    flagged: { bg: "rgba(255,68,68,0.15)", color: "#ff4444", label: "Flagged" },
  };
  const s = map[status] || map.inactive;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 6,
      background: s.bg, color: s.color,
      fontSize: 11, fontWeight: 700,
    }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.color }} />
      {s.label}
    </span>
  );
}

// ── Toast ─────────────────────────────────────────────────────────────────────
function Toast({ toast }) {
  if (!toast) return null;
  const colors = {
    success: { bg: "rgba(6,214,160,0.95)", border: "#06d6a0" },
    error: { bg: "rgba(255,68,68,0.95)", border: "#ff4444" },
    info: { bg: "rgba(72,202,228,0.95)", border: "#48cae4" },
  };
  const c = colors[toast.type] || colors.info;
  return (
    <div style={{
      position: "fixed", top: 20, right: 20, zIndex: 9999,
      background: c.bg, color: "white",
      padding: "12px 20px", borderRadius: 12,
      fontSize: 13, fontWeight: 600,
      boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
      border: `1px solid ${c.border}`,
      animation: "slideIn 0.2s ease",
    }}>{toast.msg}</div>
  );
}

function Loading() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "60px", color: "var(--text-dim)" }}>
      <div className="spinner" /> Loading...
    </div>
  );
}

function ErrorState({ message, onRetry }) {
  return (
    <div style={{ textAlign: "center", padding: "40px 20px" }}>
      <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
      <div style={{ color: "#ff4444", marginBottom: 16, fontSize: 14 }}>{message}</div>
      {onRetry && (
        <button onClick={onRetry} style={{
          padding: "8px 20px", borderRadius: 10,
          background: "rgba(255,255,255,0.08)", border: "1px solid var(--border)",
          color: "var(--text)", cursor: "pointer", fontSize: 13, fontWeight: 600,
          display: "inline-flex", alignItems: "center", gap: 6,
        }}>
          <Icon d={icons.refresh} size={14} /> Retry
        </button>
      )}
    </div>
  );
}

function Modal({ open, onClose, title, children, width = 520 }) {
  if (!open) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)",
      backdropFilter: "blur(8px)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: "var(--bg2)", border: "1px solid var(--border)",
        borderRadius: 20, width: "100%", maxWidth: width,
        maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden",
      }} onClick={e => e.stopPropagation()}>
        <div style={{
          padding: "20px 24px", borderBottom: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <span style={{ fontSize: 16, fontWeight: 700 }}>{title}</span>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.06)", border: "none",
            color: "var(--text-dim)", borderRadius: 8, width: 32, height: 32,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon d={icons.x} size={16} />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function ConfirmModal({ open, onClose, onConfirm, title, message }) {
  return (
    <Modal open={open} onClose={onClose} title={title} width={400}>
      <p style={{ fontSize: 14, color: "var(--text-dim)", marginBottom: 24 }}>{message}</p>
      <div style={{ display: "flex", gap: 10 }}>
        <button onClick={onClose} style={{
          flex: 1, padding: "10px", borderRadius: 10,
          background: "var(--bg3)", border: "1px solid var(--border)",
          color: "var(--text)", cursor: "pointer", fontWeight: 600, fontSize: 13,
        }}>Cancel</button>
        <button onClick={() => { onClose(); onConfirm(); }} style={{
          flex: 1, padding: "10px", borderRadius: 10,
          background: "rgba(255,68,68,0.15)", border: "1px solid rgba(255,68,68,0.4)",
          color: "#ff4444", cursor: "pointer", fontWeight: 600, fontSize: 13,
        }}>Confirm</button>
      </div>
    </Modal>
  );
}

// ════════════════════════════════════════════════════════════════════════
// OVERVIEW TAB
// ════════════════════════════════════════════════════════════════════════
function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [serverTime, setServerTime] = useState(new Date());

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const { data } = await api.get('/api/admin/stats');
      setStats(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load stats');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Tick clock for "Last Sync"
  useEffect(() => {
    const t = setInterval(() => setServerTime(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const activityIcon = (type) => ({
    reel_uploaded: "🎬",
    product_tagged: "🛍️",
    merchant_registered: "👤",
    reel_deleted_by_admin: "🗑️",
    reel_deleted_by_client: "🗑️",
  }[type] || "📌");

  const activityLabel = (item) => ({
    reel_uploaded: `${item.actor} uploaded a reel`,
    product_tagged: `${item.actor} tagged a product`,
    merchant_registered: `New merchant registered: ${item.actor}`,
    reel_deleted_by_admin: item.detail,
    reel_deleted_by_client: `${item.detail} by ${item.actor}`,
  }[item.type] || item.actor);

  const timeAgo = (ts) => {
    if (!ts) return "—";
    const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} onRetry={fetchStats} />;

  return (
    <div>
      {/* Stat Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px,1fr))", gap: 16, marginBottom: 24 }}>
        {[
          { icon: "🧑‍💼", label: "Total Merchants", value: stats.totalClients, sub: `${stats.activeClients} active`, color: "#ff4d6d" },
          { icon: "🎬", label: "Total Reels", value: stats.totalReels, sub: "Across all clients", color: "#c77dff" },
          { icon: "🛍️", label: "Tagged Products", value: stats.totalProducts, sub: "In reels", color: "#48cae4" },
        ].map(s => (
          <div key={s.label} style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, padding: "20px 24px", display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: s.color + "20", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{s.icon}</div>
            <div>
              <div style={{ fontSize: 26, fontWeight: 800, fontFamily: "var(--font-head)", letterSpacing: "-0.02em" }}>{s.value}</div>
              <div style={{ fontSize: 12, color: "var(--text-dim)" }}>{s.label}</div>
              <div style={{ fontSize: 11, color: s.color, marginTop: 2, fontWeight: 600 }}>{s.sub}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
        {/* Platform Health */}
        <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, padding: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 16 }}>Platform Health</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {[
              { label: "Active Merchant Rate", value: stats.totalClients > 0 ? Math.round((stats.activeClients / stats.totalClients) * 100) + "%" : "0%", color: "#06d6a0" },
              { label: "Avg Reels / Merchant", value: stats.totalClients > 0 ? (stats.totalReels / stats.totalClients).toFixed(1) : "0", color: "#c77dff" },
              { label: "Avg Products / Reel", value: stats.totalReels > 0 ? (stats.totalProducts / stats.totalReels).toFixed(1) : "0", color: "#48cae4" },
            ].map(s => (
              <div key={s.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "var(--bg3)", borderRadius: 10 }}>
                <span style={{ fontSize: 12, color: "var(--text-dim)" }}>{s.label}</span>
                <span style={{ fontSize: 16, fontWeight: 800, color: s.color, fontFamily: "var(--font-head)" }}>{s.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right column: Top Merchant + System Info */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Top Merchant */}
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, padding: 24, flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 16 }}>🏆 Top Merchant</div>
            {stats.topMerchant ? (
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ width: 48, height: 48, borderRadius: 14, background: "rgba(255,77,109,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 800, color: "var(--accent)", flexShrink: 0 }}>
                  {stats.topMerchant.username[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{stats.topMerchant.username}</div>
                  <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 2 }}>
                    <span style={{ color: "#c77dff", fontWeight: 700 }}>{stats.topMerchant.reel_count}</span> reels uploaded
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "var(--text-dim)" }}>No merchants yet</div>
            )}
          </div>

          {/* System Info */}
          <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, padding: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 14 }}>🖥️ System Info</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {[
                { label: "Server Status", value: "Online", color: "#06d6a0", dot: true },
                { label: "Database", value: "Connected", color: "#06d6a0", dot: true },
                { label: "Last Sync", value: timeAgo(serverTime), color: "var(--text-dim)", dot: false },
              ].map(s => (
                <div key={s.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 12, color: "var(--text-dim)" }}>{s.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: s.color, display: "flex", alignItems: "center", gap: 5 }}>
                    {s.dot && <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.color, display: "inline-block" }} />}
                    {s.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Recent Activity</div>
          <button onClick={fetchStats} style={{ background: "none", border: "none", color: "var(--text-dim)", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
            <Icon d={icons.refresh} size={13} /> Refresh
          </button>
        </div>
        {stats.recentActivity?.length === 0 ? (
          <div style={{ fontSize: 13, color: "var(--text-dim)", textAlign: "center", padding: "20px 0" }}>No recent activity</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {(stats.recentActivity || []).map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderRadius: 10, background: i % 2 === 0 ? "var(--bg3)" : "transparent" }}>
                <span style={{ fontSize: 16, flexShrink: 0 }}>{activityIcon(item.type)}</span>
                <span style={{ fontSize: 13, flex: 1 }}>{activityLabel(item)}</span>
                <span style={{ fontSize: 11, color: "var(--text-dim)", flexShrink: 0 }}>{timeAgo(item.ts)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// USER DIRECTORY TAB
// ════════════════════════════════════════════════════════════════════════
function UserDirectoryTab({ showToast }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    role: "client",
    username: "",
    email: "",
    phone: "",
    password: "",
  });

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const { data } = await api.get('/api/admin/clients');
      setClients(data.clients);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load clients');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const updateStatus = async (clientId, status) => {
    try {
      await api.patch(`/api/admin/clients/${clientId}/status`, { status });
      setClients(prev => prev.map(c => c.client_id === clientId ? { ...c, status } : c));
      if (selected?.client_id === clientId) setSelected(s => ({ ...s, status }));
      showToast(`Account ${status}`, "success");
    } catch (err) { showToast(err.response?.data?.error || 'Failed', "error"); }
  };

  const deleteClient = async (clientId) => {
    try {
      await api.delete(`/api/admin/clients/${clientId}`);
      setClients(prev => prev.filter(c => c.client_id !== clientId));
      setSelected(null);
      showToast("Client deleted", "success");
    } catch (err) { showToast(err.response?.data?.error || 'Failed', "error"); }
  };

  const updateCreateForm = (key, value) => {
    setCreateForm(prev => ({ ...prev, [key]: value }));
  };

  const resetCreateForm = () => {
    setCreateForm({
      role: "client",
      username: "",
      email: "",
      phone: "",
      password: "",
    });
  };

  const createUser = async () => {
    if (!createForm.username.trim() || !createForm.email.trim() || !createForm.password.trim()) {
      showToast("Username, email, and password are required", "error");
      return;
    }

    try {
      setCreating(true);

      const endpoint = createForm.role === "super_admin"
        ? "/api/auth/register/superadmin"
        : "/api/auth/register/client";

      const payload = createForm.role === "super_admin"
        ? {
          username: createForm.username.trim(),
          email: createForm.email.trim(),
          password: createForm.password,
        }
        : {
          username: createForm.username.trim(),
          email: createForm.email.trim(),
          phone: createForm.phone.trim(),
          password: createForm.password,
        };

      await api.post(endpoint, payload);

      showToast(createForm.role === "super_admin" ? "Super admin created" : "Merchant created", "success");
      setShowCreateModal(false);
      resetCreateForm();
      fetchClients();
    } catch (err) {
      showToast(err.response?.data?.error || err.response?.data?.message || "Failed to create user", "error");
    } finally {
      setCreating(false);
    }
  };

  const filtered = clients.filter(c => {
    const matchSearch = c.username?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === "all" || (c.status || "active") === statusFilter;
    return matchSearch && matchStatus;
  });

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} onRetry={fetchClients} />;

  return (
    <div>
      {confirm && <ConfirmModal open={true} onClose={() => setConfirm(null)} onConfirm={confirm.action} title={confirm.title} message={confirm.message} />}
      <Modal open={showCreateModal} onClose={() => { if (!creating) { setShowCreateModal(false); resetCreateForm(); } }} title="Add New User" width={520}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Role</div>
            <div style={{ display: "flex", gap: 8 }}>
              {[
                { value: "client", label: "Merchant" },
                { value: "super_admin", label: "Super Admin" },
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => updateCreateForm("role", option.value)}
                  type="button"
                  style={{
                    flex: 1,
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: "1px solid",
                    borderColor: createForm.role === option.value ? "var(--accent)" : "var(--border)",
                    background: createForm.role === option.value ? "rgba(255,77,109,0.15)" : "var(--bg)",
                    color: createForm.role === option.value ? "var(--accent)" : "var(--text)",
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Username</div>
            <input className="form-input" value={createForm.username} onChange={e => updateCreateForm("username", e.target.value)} placeholder="Enter username" />
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Email</div>
            <input className="form-input" type="email" value={createForm.email} onChange={e => updateCreateForm("email", e.target.value)} placeholder="Enter email address" />
          </div>

          {createForm.role === "client" && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Phone</div>
              <input className="form-input" value={createForm.phone} onChange={e => updateCreateForm("phone", e.target.value)} placeholder="Phone number" />
            </div>
          )}

          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-dim)", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Password</div>
            <input className="form-input" type="password" value={createForm.password} onChange={e => updateCreateForm("password", e.target.value)} placeholder="Set a password" />
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
            <button
              type="button"
              onClick={() => { setShowCreateModal(false); resetCreateForm(); }}
              disabled={creating}
              style={{
                flex: 1,
                padding: "10px",
                borderRadius: 10,
                background: "var(--bg3)",
                border: "1px solid var(--border)",
                color: "var(--text)",
                cursor: creating ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: 13,
                opacity: creating ? 0.6 : 1,
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={createUser}
              disabled={creating}
              className="btn btn-primary"
              style={{ flex: 1 }}
            >
              {creating ? <><span className="spinner" /> Creating...</> : "Create User"}
            </button>
          </div>
        </div>
      </Modal>

      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-dim)" }}><Icon d={icons.search} size={15} /></div>
          <input className="form-input" style={{ paddingLeft: 36 }} placeholder="Search by name or email..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {["all", "active", "suspended", "inactive"].map(f => (
          <button key={f} onClick={() => setStatusFilter(f)} style={{
            padding: "8px 14px", borderRadius: 8, border: "1px solid",
            borderColor: statusFilter === f ? "var(--accent)" : "var(--border)",
            background: statusFilter === f ? "rgba(255,77,109,0.15)" : "transparent",
            color: statusFilter === f ? "var(--accent)" : "var(--text-dim)",
            fontSize: 12, fontWeight: 600, cursor: "pointer", textTransform: "capitalize",
          }}>{f}</button>
        ))}
        <button onClick={fetchClients} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid var(--border)", background: "transparent", color: "var(--text-dim)", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          <Icon d={icons.refresh} size={14} /> Refresh
        </button>
        <button onClick={() => setShowCreateModal(true)} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid rgba(255,77,109,0.35)", background: "rgba(255,77,109,0.12)", color: "var(--accent)", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700 }}>
          + Add User
        </button>
      </div>

      <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              {["Merchant", "Contact", "Content", "Status", "Joined", "Actions"].map(h => (
                <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: "center", padding: "40px", color: "var(--text-dim)" }}>No merchants found</td></tr>
            ) : filtered.map((c, i) => (
              <tr key={c.client_id} style={{ borderBottom: i < filtered.length - 1 ? "1px solid var(--border)" : "none", opacity: (c.status || "active") === "suspended" ? 0.6 : 1 }}
                onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.02)"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "14px 16px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: "var(--bg3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: "var(--accent)" }}>{(c.username || "?")[0].toUpperCase()}</div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{c.username}</div>
                  </div>
                </td>
                <td style={{ padding: "14px 16px" }}>
                  <div style={{ fontSize: 12, color: "var(--text-dim)" }}>{c.email}</div>
                  {c.phone && <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 2 }}>{c.phone}</div>}
                </td>
                <td style={{ padding: "14px 16px" }}>
                  <span style={{ color: "var(--accent)", fontWeight: 600, fontSize: 12 }}>{c.reel_count}</span>
                  <span style={{ color: "var(--text-dim)", fontSize: 12 }}> reels · </span>
                  <span style={{ color: "#48cae4", fontWeight: 600, fontSize: 12 }}>{c.product_count}</span>
                  <span style={{ color: "var(--text-dim)", fontSize: 12 }}> products</span>
                </td>
                <td style={{ padding: "14px 16px" }}><StatusBadge status={c.status || "active"} /></td>
                <td style={{ padding: "14px 16px", fontSize: 12, color: "var(--text-dim)" }}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : "—"}</td>
                <td style={{ padding: "14px 16px" }}>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => setSelected(c)} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid var(--border)", color: "var(--text-dim)", borderRadius: 8, padding: "6px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}>
                      <Icon d={icons.eye} size={13} /> View
                    </button>
                    {(c.status || "active") === "active"
                      ? <button onClick={() => setConfirm({ title: "Suspend Account", message: `Suspend ${c.username}? They will lose access immediately.`, action: () => updateStatus(c.client_id, "suspended") })} style={{ background: "rgba(255,68,68,0.1)", border: "1px solid rgba(255,68,68,0.3)", color: "#ff4444", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Suspend</button>
                      : <button onClick={() => updateStatus(c.client_id, "active")} style={{ background: "rgba(6,214,160,0.1)", border: "1px solid rgba(6,214,160,0.3)", color: "#06d6a0", borderRadius: 8, padding: "6px 10px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Restore</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Merchant Profile" width={560}>
        {selected && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: "var(--bg3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 800, color: "var(--accent)" }}>{(selected.username || "?")[0].toUpperCase()}</div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>{selected.username}</div>
                <StatusBadge status={selected.status || "active"} />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
              {[["Email", selected.email], ["Joined", selected.created_at ? new Date(selected.created_at).toLocaleDateString() : "—"], ["Manual Upload", selected.manual_upload ? "✅ Enabled" : "❌ Disabled"], ["Reels", selected.reel_count], ["Tagged Products", selected.product_count]].map(([k, v]) => (
                <div key={k} style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 14px" }}>
                  <div style={{ fontSize: 10, color: "var(--text-dim)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{k}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              {(selected.status || "active") === "active"
                ? <button onClick={() => setConfirm({ title: "Suspend Account", message: `Suspend ${selected.username}?`, action: () => updateStatus(selected.client_id, "suspended") })} style={{ flex: 1, padding: "10px", borderRadius: 10, background: "rgba(255,68,68,0.1)", border: "1px solid rgba(255,68,68,0.3)", color: "#ff4444", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Suspend Account</button>
                : <button onClick={() => updateStatus(selected.client_id, "active")} style={{ flex: 1, padding: "10px", borderRadius: 10, background: "rgba(6,214,160,0.1)", border: "1px solid rgba(6,214,160,0.3)", color: "#06d6a0", cursor: "pointer", fontWeight: 600, fontSize: 13 }}>Restore Account</button>}
              <button onClick={() => { setSelected(null); setConfirm({ title: "Delete Client", message: `Permanently delete ${selected.username} and ALL their data? This cannot be undone.`, action: () => deleteClient(selected.client_id) }); }} style={{ padding: "10px 16px", borderRadius: 10, background: "rgba(255,68,68,0.15)", border: "1px solid rgba(255,68,68,0.4)", color: "#ff4444", cursor: "pointer", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                <Icon d={icons.trash} size={14} /> Delete
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// MEDIA LIBRARY TAB
// ════════════════════════════════════════════════════════════════════════
function MediaLibraryTab({ showToast }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [clientFilter, setClientFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const fetchMedia = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const { data } = await api.get('/api/admin/media');
      setMedia(data.media);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load media');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMedia(); }, [fetchMedia]);

  const updateStatus = async (videoId, status) => {
    try {
      await api.patch(`/api/admin/media/${videoId}/status`, { status });
      setMedia(prev => prev.map(m => m.video_id === videoId ? { ...m, status } : m));
      if (selected?.video_id === videoId) setSelected(s => ({ ...s, status }));
      showToast(`Media marked as ${status}`, "success");
    } catch (err) { showToast(err.response?.data?.error || 'Failed', "error"); }
  };

  const deleteMedia = async (videoId) => {
    try {
      await api.delete(`/api/admin/media/${videoId}`);
      setMedia(prev => prev.filter(m => m.video_id !== videoId));
      setSelected(null);
      showToast("Media deleted", "success");
    } catch (err) { showToast(err.response?.data?.error || 'Failed', "error"); }
  };

  const clientNames = [...new Set(media.map(m => m.client_name))].filter(Boolean);
  const filtered = media.filter(m => {
    const matchSearch = m.caption?.toLowerCase().includes(search.toLowerCase()) || m.client_name?.toLowerCase().includes(search.toLowerCase()) || m.video_id?.toLowerCase().includes(search.toLowerCase());
    const matchClient = clientFilter === "all" || m.client_name === clientFilter;
    const matchSource = sourceFilter === "all" || (m.upload_source || "manual") === sourceFilter;
    return matchSearch && matchClient && matchSource;
  });

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} onRetry={fetchMedia} />;

  return (
    <div>
      {confirm && <ConfirmModal open={true} onClose={() => setConfirm(null)} onConfirm={confirm.action} title={confirm.title} message={confirm.message} />}



      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-dim)" }}><Icon d={icons.search} size={15} /></div>
          <input className="form-input" style={{ paddingLeft: 36 }} placeholder="Search media..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 180 }} value={clientFilter} onChange={e => setClientFilter(e.target.value)}>
          <option value="all">All Merchants</option>
          {clientNames.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <select className="form-select" style={{ width: 160 }} value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
          <option value="all">All Sources</option>
          <option value="instagram">📱 Instagram</option>
          <option value="downloaded">⬇️ Downloaded</option>
          <option value="url_fetched">🔗 URL Fetched</option>
          <option value="manual">📤 Manual</option>
        </select>
        <button onClick={fetchMedia} style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid var(--border)", background: "transparent", color: "var(--text-dim)", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          <Icon d={icons.refresh} size={14} /> Refresh
        </button>
      </div>

      <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              {["Reel", "Merchant", "Source", "Tags", "Date", "Actions"].map(h => (
                <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0
              ? <tr><td colSpan={7} style={{ textAlign: "center", padding: "40px", color: "var(--text-dim)" }}>No media found</td></tr>
              : filtered.map((m, i) => (
                <tr key={m.video_id} style={{ borderBottom: i < filtered.length - 1 ? "1px solid var(--border)" : "none", borderLeft: m.status === "flagged" ? "3px solid #ff4444" : "3px solid transparent" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.02)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ fontSize: 12, fontWeight: 600, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.caption?.slice(0, 40) || m.video_id?.slice(0, 20) + "..." || "—"}</div>
                    <div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 2 }}>{m.media_type || "VIDEO"}</div>
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 12 }}>{m.client_name}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <span style={{ 
                      fontSize: 11, 
                      fontWeight: 600, 
                      color: m.upload_source === "instagram" ? "#c77dff" : 
                             m.upload_source === "downloaded" ? "#48cae4" : 
                             m.upload_source === "url_fetched" ? "var(--accent)" : "#06d6a0" 
                    }}>
                      {m.upload_source === "instagram" ? "📱 Instagram" : 
                       m.upload_source === "downloaded" ? "⬇️ Downloaded" : 
                       m.upload_source === "url_fetched" ? "🔗 URL Fetched" : "📤 Manual"}
                    </span>
                  </td>
                  <td style={{ padding: "12px 16px", fontSize: 12, color: "var(--accent)", fontWeight: 600 }}>{m.tag_count}</td>
                  <td style={{ padding: "12px 16px", fontSize: 11, color: "var(--text-dim)" }}>{m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}</td>
                  <td style={{ padding: "12px 16px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => setSelected(m)} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid var(--border)", color: "var(--text-dim)", borderRadius: 8, padding: "5px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontSize: 12 }}><Icon d={icons.eye} size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Media Details" width={520}>
        {selected && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 20 }}>
              {[["Video ID", (selected.video_id || "").slice(0, 22) + "..."], ["Merchant", selected.client_name], ["Source", selected.upload_source || "—"], ["Media Type", selected.media_type || "VIDEO"], ["Tagged Products", selected.tag_count], ["Caption", (selected.caption || "—").slice(0, 40)], ["Created", selected.created_at ? new Date(selected.created_at).toLocaleString() : "—"]].map(([k, v]) => (
                <div key={k} style={{ background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 10, padding: "10px 14px" }}>
                  <div style={{ fontSize: 10, color: "var(--text-dim)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{k}</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{v}</div>
                </div>
              ))}
            </div>
            {selected.media_url && (
              <div style={{ marginBottom: 16 }}>
                <a
                  href={selected.media_url.startsWith('http') ? selected.media_url : `${process.env.REACT_APP_BASE_URL}${selected.media_url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 12, color: "var(--accent)", textDecoration: "none", fontWeight: 600 }}
                  onClick={e => {
                    e.preventDefault();
                    window.open(
                      selected.media_url.startsWith('http') ? selected.media_url : `${process.env.REACT_APP_BASE_URL}${selected.media_url}`,
                      '_blank',
                      'noopener,noreferrer'
                    );
                  }}
                >🔗 View Media File</a>
              </div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => { setSelected(null); setConfirm({ title: "Delete Media", message: "Permanently delete this reel and all its product tags?", action: () => deleteMedia(selected.video_id) }); }} style={{ padding: "10px 16px", borderRadius: 10, background: "rgba(255,68,68,0.15)", border: "1px solid rgba(255,68,68,0.4)", color: "#ff4444", cursor: "pointer", fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
                <Icon d={icons.trash} size={14} /> Delete
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// ACCESS CONTROL TAB
// ════════════════════════════════════════════════════════════════════════
function AccessControlTab({ showToast }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(null);

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const { data } = await api.get('/api/admin/clients');
      setClients(data.clients);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const toggleManualUpload = async (clientId, enabled) => {
    setSaving(clientId);
    try {
      await api.patch(`/api/admin/clients/${clientId}/manual-upload`, { enabled });
      setClients(prev => prev.map(c => c.client_id === clientId ? { ...c, manual_upload: enabled } : c));
      showToast(`Manual upload ${enabled ? "enabled" : "disabled"}`, "success");
    } catch (err) { showToast(err.response?.data?.error || 'Failed', "error"); }
    finally { setSaving(null); }
  };

  const bulkToggle = async (enabled) => {
    try {
      await Promise.all(clients.map(c => api.patch(`/api/admin/clients/${c.client_id}/manual-upload`, { enabled })));
      setClients(prev => prev.map(c => ({ ...c, manual_upload: enabled })));
      showToast(`Manual upload ${enabled ? "enabled" : "disabled"} for all`, "success");
    } catch { showToast("Bulk update failed", "error"); }
  };

  const filtered = clients.filter(c =>
    c.username?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <Loading />;
  if (error) return <ErrorState message={error} onRetry={fetchClients} />;

  return (
    <div>
      <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, padding: 24, marginBottom: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>Bulk Actions</div>
        <div style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 16 }}>Apply manual upload access changes to all merchants at once.</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={() => bulkToggle(true)} style={{ padding: "8px 16px", borderRadius: 10, background: "rgba(6,214,160,0.1)", border: "1px solid rgba(6,214,160,0.3)", color: "#06d6a0", cursor: "pointer", fontWeight: 600, fontSize: 12 }}>✅ Enable All</button>
          <button onClick={() => bulkToggle(false)} style={{ padding: "8px 16px", borderRadius: 10, background: "rgba(255,68,68,0.1)", border: "1px solid rgba(255,68,68,0.3)", color: "#ff4444", cursor: "pointer", fontWeight: 600, fontSize: 12 }}>🔒 Disable All</button>
        </div>
      </div>

      <div style={{ position: "relative", marginBottom: 16 }}>
        <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-dim)" }}><Icon d={icons.search} size={15} /></div>
        <input className="form-input" style={{ paddingLeft: 36 }} placeholder="Search merchants..." value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--border)" }}>
              {["Merchant", "Email", "Manual Upload", "Account Status"].map(h => (
                <th key={h} style={{ padding: "12px 20px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", letterSpacing: "0.05em" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0
              ? <tr><td colSpan={5} style={{ textAlign: "center", padding: "40px", color: "var(--text-dim)" }}>No merchants found</td></tr>
              : filtered.map((c, i) => (
                <tr key={c.client_id} style={{ borderBottom: i < filtered.length - 1 ? "1px solid var(--border)" : "none", opacity: (c.status || "active") === "suspended" ? 0.5 : 1 }}>
                  <td style={{ padding: "16px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 34, height: 34, borderRadius: 9, background: "var(--bg3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "var(--accent)" }}>{(c.username || "?")[0].toUpperCase()}</div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{c.username}</div>
                    </div>
                  </td>
                  <td style={{ padding: "16px 20px", fontSize: 12, color: "var(--text-dim)" }}>{c.email}</td>
                  <td style={{ padding: "16px 20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Toggle value={!!c.manual_upload} onChange={v => toggleManualUpload(c.client_id, v)} disabled={saving === c.client_id || (c.status || "active") === "suspended"} />
                      {saving === c.client_id && <div className="spinner" style={{ width: 12, height: 12 }} />}
                      <span style={{ fontSize: 11, color: c.manual_upload ? "#06d6a0" : "var(--text-dim)" }}>{c.manual_upload ? "Enabled" : "Disabled"}</span>
                    </div>
                  </td>
                  <td style={{ padding: "16px 20px" }}><StatusBadge status={c.status || "active"} /></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// UPLOAD CONTROLS TAB
// ════════════════════════════════════════════════════════════════════════
function SettingsTab({ showToast }) {
  const [allowUpload, setAllowUpload] = useState(true);
  const [limits, setLimits] = useState({ maxReels: 100, maxFileSizeMb: 200, maxDurationSec: 180 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await api.get('/api/admin/settings');
        if (data.allow_reel_upload !== undefined) setAllowUpload(data.allow_reel_upload);
        if (data.upload_limits) setLimits(data.upload_limits);
      } catch (err) {
        showToast('Failed to load settings', 'error');
      } finally { setLoading(false); }
    };
    fetchSettings();
  }, []);

  const updateLimits = (key, val) => setLimits(s => ({ ...s, [key]: Number(val) }));

  const saveLimits = async () => {
    setSaving(true);
    try {
      await api.patch('/api/admin/settings', { key: 'upload_limits', value: limits });
      showToast('Upload limits saved', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save', 'error');
    } finally { setSaving(false); }
  };

  const toggleUpload = async (val) => {
    setSavingToggle(true);
    setAllowUpload(val);
    try {
      await api.patch('/api/admin/settings', { key: 'allow_reel_upload', value: val });
      showToast(`Reel upload ${val ? 'enabled' : 'disabled'} for all merchants`, val ? 'success' : 'info');
    } catch (err) {
      setAllowUpload(!val);
      showToast(err.response?.data?.error || 'Failed to update', 'error');
    } finally { setSavingToggle(false); }
  };

  if (loading) return <Loading />;

  return (
    <div style={{ maxWidth: 620 }}>
      {/* Allow Reel Upload Toggle */}
      <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, padding: 24, marginBottom: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 16 }}>Reel Upload Access</div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              Allow Reel Upload
              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 6, background: allowUpload ? "rgba(6,214,160,0.15)" : "rgba(255,68,68,0.15)", color: allowUpload ? "#06d6a0" : "#ff4444" }}>
                {allowUpload ? "ON" : "OFF"}
              </span>
            </div>
            <div style={{ fontSize: 12, color: "var(--text-dim)", marginTop: 2 }}>
              {allowUpload ? "Merchants can currently upload reels" : "All reel uploads are currently blocked for all merchants"}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {savingToggle && <div className="spinner" style={{ width: 14, height: 14 }} />}
            <Toggle value={allowUpload} onChange={toggleUpload} disabled={savingToggle} accent="#06d6a0" />
          </div>
        </div>
      </div>

      {/* Upload Limits */}
      <div style={{ background: "var(--bg2)", border: "1px solid var(--border)", borderRadius: 16, padding: 24 }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Upload Limits</div>
        <div style={{ fontSize: 13, color: "var(--text-dim)", marginBottom: 20 }}>Set maximum allowed values for merchant uploads.</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 24 }}>
          {[
            { label: "Max Reels per Client", key: "maxReels", unit: "reels" },
            { label: "Max File Size", key: "maxFileSizeMb", unit: "MB" },
            { label: "Max Video Duration", key: "maxDurationSec", unit: "seconds" },
          ].map(f => (
            <div key={f.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 2 }}>{f.label}</div>
                <div style={{ fontSize: 11, color: "var(--text-dim)" }}>Current: {limits[f.key]} {f.unit}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  className="form-input"
                  type="number"
                  min={1}
                  value={limits[f.key]}
                  onChange={e => updateLimits(f.key, e.target.value)}
                  style={{ width: 110, textAlign: "center" }}
                />
                <span style={{ fontSize: 12, color: "var(--text-dim)", minWidth: 50 }}>{f.unit}</span>
              </div>
            </div>
          ))}
        </div>
        <button onClick={saveLimits} disabled={saving} className="btn btn-primary" style={{ width: "100%" }}>
          {saving ? <><span className="spinner" /> Saving...</> : 'Save Limits'}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════════════
export default function SuperAdminDashboard() {
  const [activeTab, setActiveTab] = useState("overview");
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const tabs = [
    { id: "overview", icon: icons.dashboard, label: "Overview" },
    { id: "users", icon: icons.users, label: "User Directory" },
    { id: "media", icon: icons.media, label: "Media Library" },
    { id: "access", icon: icons.access, label: "Access Control" },
    { id: "settings", icon: icons.settings, label: "Upload Controls" },
  ];

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", fontFamily: "var(--font-body)" }}>
      <style>{`@keyframes slideIn { from { opacity:0; transform: translateX(20px); } to { opacity:1; transform: translateX(0); } }`}</style>
      <Toast toast={toast} />
      <div style={{ display: "flex", minHeight: "100vh" }}>
        <aside style={{ width: 240, background: "var(--bg2)", borderRight: "1px solid var(--border)", position: "fixed", height: "100vh", left: 0, top: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "20px 20px 22px", borderBottom: "1px solid var(--border)" }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 20, fontWeight: 800, letterSpacing: "-0.02em" }}>
              <span style={{ color: "var(--accent)" }}>Vids</span>Commerce
            </div>
            <div style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 6, background: "rgba(255,214,10,0.12)", border: "1px solid rgba(255,214,10,0.25)", borderRadius: 8, padding: "4px 10px", fontSize: 11, fontWeight: 700, color: "#ffd60a" }}>⚡ Super Admin</div>
          </div>
          <nav style={{ flex: 1, padding: 10 }}>
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", borderRadius: 10, background: activeTab === t.id ? "var(--accent)" : "none", border: "none", color: activeTab === t.id ? "white" : "rgba(255,255,255,0.55)", width: "100%", cursor: "pointer", fontSize: 13, fontWeight: 600, transition: "all 0.2s", marginBottom: 2, textAlign: "left" }}
                onMouseEnter={e => { if (activeTab !== t.id) e.currentTarget.style.background = "rgba(255,255,255,0.05)"; }}
                onMouseLeave={e => { if (activeTab !== t.id) e.currentTarget.style.background = "none"; }}>
                <Icon d={t.icon} size={16} />{t.label}
              </button>
            ))}
          </nav>
          <div style={{ padding: 10, borderTop: "1px solid var(--border)" }}>
            <div style={{ padding: "10px 14px", marginBottom: 4 }}>
              <div style={{ fontSize: 11, color: "var(--text-dim)", marginBottom: 2 }}>Signed in as</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Super Admin</div>
            </div>
            <button onClick={() => { localStorage.removeItem('vc_token'); localStorage.removeItem('vc_user'); window.location.href = '/login'; }} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, background: "none", border: "none", color: "rgba(255,255,255,0.45)", width: "100%", cursor: "pointer", fontSize: 13 }}>
              <Icon d={icons.logout} size={16} /> Log out
            </button>
          </div>
        </aside>
        <main style={{ marginLeft: 240, flex: 1, padding: "32px 40px" }}>
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800, letterSpacing: "-0.02em", marginBottom: 4 }}>{tabs.find(t => t.id === activeTab)?.label}</div>
            <div style={{ fontSize: 13, color: "var(--text-dim)" }}>{{ overview: "Platform-wide statistics", users: "All registered merchants", media: "All reels across merchants", access: "Manage feature access per merchant", settings: "Control upload permissions and limits" }[activeTab]}</div>
          </div>
          {activeTab === "overview" && <OverviewTab />}
          {activeTab === "users" && <UserDirectoryTab showToast={showToast} />}
          {activeTab === "media" && <MediaLibraryTab showToast={showToast} />}
          {activeTab === "access" && <AccessControlTab showToast={showToast} />}
          {activeTab === "settings" && <SettingsTab showToast={showToast} />}
        </main>
      </div>
    </div>
  );
}
